import { PermissionUser } from "./permission-user";
import { Role } from "./role";

export class User {
  first_name!: string;
  middle_name?: string;
  last_name!: string;
  password?: string;
  affiliation!: string;
  title?: string;
  email!: string;
  remarks?: string;
  role_uuid!: string;
  street_address?: string;
  city?: string;
  country?: string;
  phone_number?: string;
  secondary_phone_number?: string;
  date_joined?: number;
  date_left?: number;
  gender?: string;
  national_id_number?: string;
  next_of_kin_name?: string;
  next_of_kin_phone_number?: string;
  next_of_kin_national_id_number?: string;
  next_of_kin_relationship?: string;
  signature_path?: string;
  national_id_path?: string;
  avatar_path?: string;
  status!: string;
  uuid!: string;
  // Signed token the backend issues (uuid + HMAC). Sent as ?ref= on public document
  // links so the printed doc can show "Printed by <name>" without exposing a raw,
  // swappable user uuid (which would be an unauthenticated uuid->name oracle).
  print_ref?: string;
  date_deleted?: number;
  date_created!: number;
  date_updated!: number;

  role!: Role;
  user?: User;
  permission_users?: Array<PermissionUser>;


  getFullName(): string {
    return [this.first_name, this.middle_name, this.last_name].filter(Boolean).join(' ');
  }

  /** Signed ref for public document links. Empty (→ "--" on the doc) if not issued yet. */
  printRef(): string {
    return this.print_ref ?? '';
  }

  isActive(): boolean {
    return (this.status || '').toLowerCase() === 'active';
  }

  static getSortColumns() {
    return [
      'first_name',
      'middle_name',
      'last_name',
      'email',
      'affiliation',
      'city',
      'country',
      'phone_number',
      'secondary_phone_number',
      'status',
    ];
  }

  constructor(data: any = {}) {
    // Timestamps
    this.date_created = data.date_created;
    this.date_updated = data.date_updated;
    this.date_deleted = data.date_deleted;

    // Identity & contact
    this.uuid = data.uuid;
    this.print_ref = data.print_ref;
    this.first_name = data.first_name;
    this.middle_name = data.middle_name;
    this.last_name = data.last_name;
    this.email = data.email;
    this.phone_number = data.phone_number;
    this.secondary_phone_number = data.secondary_phone_number;

    // Account & HR
    this.password = data.password; // avoid populating on client if possible
    this.affiliation = data.affiliation;
    this.title = data.title;
    this.remarks = data.remarks;
    this.role_uuid = data.role_uuid;
    this.status = data.status;

    // Address
    this.street_address = data.street_address;
    this.city = data.city;
    this.country = data.country;

    // Dates
    this.date_joined = data.date_joined;
    this.date_left = data.date_left;

    // Personal IDs & next of kin
    this.gender = data.gender;
    this.national_id_number = data.national_id_number;
    this.next_of_kin_name = data.next_of_kin_name;
    this.next_of_kin_phone_number = data.next_of_kin_phone_number;
    this.next_of_kin_national_id_number = data.next_of_kin_national_id_number;
    this.next_of_kin_relationship = data.next_of_kin_relationship;

    // Files
    this.signature_path = data.signature_path;
    this.national_id_path = data.national_id_path;
    this.avatar_path = data.avatar_path;


    if (data.user) {
      this.user = new User(data.user);
    }

    if (data.role) {
      this.role = new Role(data.role);
    }

    if (data.permission_users) {
      this.permission_users = data.permission_users.map((p: any) => new PermissionUser(p));
    }
  }
}
